import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import { DeleteOutlined, MoreVertOutlined, PrintOutlined } from "@mui-symbols-material/w300"
import type { FC, MouseEvent } from "react"
import { useRef } from "react"

import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { isPalletAs400SyncPending } from "@/screens/WarehouseReceiving/utils"

interface GoodsReceiptPalletActionsCellProps {
  pallet: GoodsReceiptPallet
  shouldShowQrPrint?: boolean
  shouldShowDelete?: boolean
  isDeleteDisabled?: boolean
  menuOpen?: boolean
  onMenuOpen?: (palletId: string) => void
  onMenuClose?: () => void
  onQrPrint?: (pallet: GoodsReceiptPallet) => void
  onDelete?: (pallet: GoodsReceiptPallet) => void
}

export const GoodsReceiptPalletActionsCell: FC<GoodsReceiptPalletActionsCellProps> = ({
  pallet,
  shouldShowQrPrint = true,
  shouldShowDelete = false,
  isDeleteDisabled = false,
  menuOpen = false,
  onMenuOpen,
  onMenuClose,
  onQrPrint,
  onDelete,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)

  const handleStopPropagation = (e: MouseEvent) => {
    e.stopPropagation()
  }

  const handleQrPrint = () => {
    onQrPrint?.(pallet)
  }

  const handleDelete = () => {
    onMenuClose?.()
    onDelete?.(pallet)
  }

  return (
    <Box className='flex items-center justify-end gap-x-3' ref={anchorElRef} onClick={handleStopPropagation}>
      {shouldShowQrPrint && (
        <IconButton onClick={handleQrPrint} data-testid='print-qr-button'>
          <PrintOutlined />
        </IconButton>
      )}
      {shouldShowDelete && (
        <>
          <IconButton
            onClick={() => onMenuOpen?.(pallet.goodsReceiptPalletId)}
            data-testid='pallet-actions-menu-button'
          >
            <MoreVertOutlined />
          </IconButton>
          <Menu anchorEl={anchorElRef.current} open={menuOpen} onClose={onMenuClose} data-testid='pallet-actions-menu'>
            <MenuItem
              data-testid='delete-pallet-menu-item'
              onClick={handleDelete}
              disabled={isPalletAs400SyncPending(pallet) || isDeleteDisabled}
            >
              <ListItemIcon>
                <DeleteOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
              </ListItemIcon>
              <ListItemText primary='Delete' />
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  )
}
